<template>
	<AppLayout :title="trans('settings.languages.title')">
		<Modal id="create-language-modal">
			<template #title>{{ trans('settings.languages.new.title') }}</template>
			<LabeledObject required>
				<template #label>{{ trans('settings.languages.name') }}</template>
				<input v-model="newLanguage.name" type="text">
			</LabeledObject>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="store">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="showModal('create-language-modal')">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<Empty v-if="languages.length === 0"/>
		<CardGrid v-else>
			<Card v-for="language in languages" :key="`companies-${language.id}`">
				<LabeledObject required>
					<template #label>{{ trans('settings.languages.name') }}</template>
					<input v-model="language.name" type="text">
				</LabeledObject>
				<LabeledObject>
					<template #label>{{ trans('settings.languages.students') }}</template>
					<p>{{ language.students_count }}</p>
				</LabeledObject>
				<template #footer>
					<SecondaryButton :color="Color.Green" @click.stop="save(language)">
						<i class="fa-solid fa-save mr-2"/>
						{{ trans('general.save') }}
					</SecondaryButton>
					<SoftButton v-if="language.students_count === 0" :color="Color.Red" @click.stop="destroy(language)">
						<i class="fa-solid fa-trash mr-2"/>
						{{ trans('general.delete') }}
					</SoftButton>
				</template>
			</Card>
		</CardGrid>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import {defineProps, type Ref, ref} from 'vue'
import Card from '@/Components/UI/Cards/Card.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import {type Language} from '@/Types/Settings/Language'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	languages:Array<Language>,
}>()

const working:Ref<boolean> = ref(false)
const languages:Ref<Array<Language>> = ref(props.languages)
const newLanguage:Ref<Language> = ref({
	name: '',
} as Language)

function store():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('settings.languages.store'), newLanguage.value).then(({data}:{data:Language}) => {
			languages.value.push(data)
			showSuccess(trans('general.inserted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function save(language:Language):void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('settings.languages.update', language.id), language).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function destroy(language:Language):void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('settings.languages.delete', language.id)).then(() => {
			const index = languages.value.findIndex((l:Language) => l.id === language.id)
			if(index > -1){
				languages.value.splice(index, 1)
			}
			showSuccess(trans('general.deleted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>